import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { usePaymentQueries } from "@/hooks/usePaymentQueries";
import { BusyOverlay } from "@/components/common/BusyOverlay";

/** The verification_notes column's cap, mirrored so an over-long note is caught before the request. */
const MAX_LENGTH = 1000;

interface PaymentNoteDialogProps {
  paymentId: string;
  /** Shown in the header so the operator can see which payment they are annotating. */
  paymentOrderNumber: string;
  /** The note as it stands, or undefined when the payment carries none. */
  note?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mirrors the mutation's pending state up, for a caller that disables its own trigger. */
  onPendingChange?: (pending: boolean) => void;
  /**
   * Whether the busy overlay covers the whole page while the write runs. A table that opens this
   * passes `false` and covers its own Card instead.
   */
  pageOverlay?: boolean;
}

/**
 * Writes the payment's note.
 *
 * Owns its own mutation — like `RefundPaymentDialog` — so the list row and the detail card open
 * the same dialog rather than each wiring up the write. Available in EVERY status, Completed
 * included: the note records what an operator knows about the payment, and most of that is learnt
 * after the money has settled. The endpoint behind it is SuperAdmin-only, so every call site gates
 * the trigger on that role.
 *
 * The note shares one field with the decisions (`verificationNotes`), which is why the dialog says
 * so up front: the customer reads this text, and a later verify / reject / cancel / refund
 * replaces it.
 */
export function PaymentNoteDialog({
  paymentId,
  paymentOrderNumber,
  note,
  open,
  onOpenChange,
  onPendingChange,
  pageOverlay = true,
}: PaymentNoteDialogProps) {
  const { updatePaymentNote } = usePaymentQueries();
  const [draft, setDraft] = useState(note ?? "");

  const isPending = updatePaymentNote.isPending;

  // Seeded when the dialog opens, not on every render: while it is open the operator's typing is
  // the source of truth, and a refetch landing underneath must not overwrite it.
  useEffect(() => {
    if (open) {
      setDraft(note ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    onPendingChange?.(isPending);
  }, [isPending, onPendingChange]);

  // Released on unmount as well, like `RefundPaymentDialog`: the list mounts this only while it is
  // open, and a successful save closes it in the same render that clears `isPending` — so the
  // effect above never reports `false` and the table's "Saving note…" overlay stayed up until F5.
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  const stored = note ?? "";
  const trimmed = draft.trim();
  const isDirty = trimmed !== stored.trim();
  const isTooLong = trimmed.length > MAX_LENGTH;
  const clearsTheNote = trimmed.length === 0 && stored.trim().length > 0;

  const handleSave = () => {
    if (!isDirty || isTooLong || isPending) {
      return;
    }

    updatePaymentNote.mutate(
      // Blank means "no note" — the API reads null and clears the row, author and timestamp with
      // it, rather than storing a note of no words.
      { paymentId, note: trimmed.length > 0 ? trimmed : null },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty,
    isSubmitting: isPending,
    onDiscard: () => setDraft(stored),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{stored ? "Edit note" : "Add note"}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <DialogDescription>
              The note on payment {paymentOrderNumber}.
            </DialogDescription>
            {/* Both consequences of sharing one field with the decisions, said before the
                operator types rather than discovered afterwards. */}
            <div className="rounded-md border border-warn/45 bg-warn-soft p-2.5 text-xs text-muted-foreground">
              This note is stored with the payment's verification notes, so{" "}
              <span className="font-medium text-foreground">
                the customer can see it
              </span>{" "}
              on their own payment screen, and verifying, rejecting, cancelling
              or refunding the payment will replace it.
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="payment-note">Note</Label>
              <span
                className={
                  isTooLong
                    ? "text-xs text-destructive-ink"
                    : "text-xs text-muted-foreground"
                }
              >
                {trimmed.length}/{MAX_LENGTH}
              </span>
            </div>
            <Textarea
              id="payment-note"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="A note about this payment — the customer can read it…"
              rows={5}
              disabled={isPending}
              className={isTooLong ? "border-destructive" : ""}
              aria-invalid={isTooLong}
            />
            {isTooLong && (
              <p className="text-sm text-destructive-ink" role="alert">
                Note cannot exceed {MAX_LENGTH} characters.
              </p>
            )}
            {/* Said plainly rather than left to the button's label alone: clearing the box and
                saving removes the note, its author and its timestamp. */}
            {clearsTheNote && !isTooLong && (
              <p className="text-sm text-muted-foreground">
                Saving an empty note removes the existing one.
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isPending || !isDirty || isTooLong}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {clearsTheNote ? "Remove note" : "Save note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
      <BusyOverlay open={isPending && pageOverlay} label="Saving note…" />
    </>
  );
}
